import { animate, motion, useInView, useReducedMotion } from 'framer-motion';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { OptimizedImage } from './OptimizedImage';
import { defaultPartners } from '../data/defaultPartners';

interface Stat {
  value: number;
  suffix: string;
  label: string;
  href: string;
}

// Figure that counts up once it scrolls into view.
const StatCounter = ({ stat }: { stat: Stat }) => {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduceMotion = useReducedMotion();
  const [display, setDisplay] = useState(reduceMotion ? stat.value : 0);

  useEffect(() => {
    if (!inView || reduceMotion) return;
    const controls = animate(0, stat.value, {
      duration: 1.4,
      ease: 'easeOut',
      onUpdate: value => setDisplay(Math.round(value)),
    });
    return () => controls.stop();
  }, [inView, reduceMotion, stat.value]);

  return (
    <a ref={ref} href={stat.href} className="group block text-center">
      <span className="block text-4xl md:text-5xl font-extrabold tracking-tight">
        {display.toLocaleString('fr-FR')}{stat.suffix}
      </span>
      <span className="mt-1 block text-sm md:text-base font-medium opacity-85 group-hover:opacity-100 transition-opacity">
        {stat.label}
      </span>
    </a>
  );
};

const Hero = () => {
  const reduceMotion = useReducedMotion();

  const stats: Stat[] = [
    { value: 10000, suffix: '+', label: 'Repas distribués', href: '#mission' },
    { value: 80, suffix: '+', label: 'Bénévoles actifs', href: '#benevole' },
    { value: defaultPartners.length, suffix: '', label: 'Partenaires engagés', href: '#partenaires' },
  ];

  // Landscape photos only: portrait shots lose the faces in a wide banner.
  // `position` keeps the people in frame when the banner crops the photo.
  const images = useMemo(() => [
    {
      src: "distribution-avenue.webp",
      alt: "Des bénévoles de Nous'Rire échangent avec une personne pendant une distribution",
      position: "object-[45%_55%]"
    },
    {
      src: "benevoles-chariots.webp",
      alt: "Deux bénévoles en route pour une distribution avec leurs chariots",
      position: "object-[50%_55%]"
    },
    {
      src: "equipe-passerelle.webp",
      alt: "L'équipe de bénévoles traverse une passerelle à Paris",
      position: "object-[50%_45%]"
    }
  ], []);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);


  // Reference to track and manage the carousel interval
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset the carousel timer to prevent immediate image change after manual navigation
  const resetTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setCurrentImageIndex(prevIndex =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
  }, [images.length]);

  // Manual carousel navigation with timer reset
  const goToNextImage = useCallback(() => {
    setCurrentImageIndex(prevIndex =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    resetTimer();
  }, [images.length, resetTimer]);

  const goToPreviousImage = useCallback(() => {
    setCurrentImageIndex(prevIndex =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    resetTimer();
  }, [images.length, resetTimer]);

  const goToSpecificImage = useCallback((index: number) => {
    setCurrentImageIndex(index);
    resetTimer();
  }, [resetTimer]);

  // Initialize carousel timer on component mount
  useEffect(() => {
    resetTimer();

    // Cleanup interval on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [resetTimer]);

  return (
    <div>
      <div className="relative min-h-[88vh] md:min-h-[90vh] flex items-end md:items-center overflow-hidden bg-brand-pink-dark">
        {/* Photo carousel */}
        {images.map((image, index) => (
          <motion.div
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentImageIndex ? 1 : 0 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
            aria-hidden={index !== currentImageIndex}
          >
            <OptimizedImage
              src=""
              imageName={image.src}
              alt={image.alt}
              width={1600}
              height={1000}
              className={`w-full h-full object-cover ${image.position}`}
            />
          </motion.div>
        ))}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/75 via-black/45 to-black/10" />

        <div className="relative container mx-auto px-4 pt-32 pb-24 md:py-32">
          <motion.div
            className="max-w-2xl text-white"
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] text-white/80 mb-4">
              Association solidaire · Paris et banlieue
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight leading-[1.08] mb-6">
              Des repas, des vêtements et du lien, dans la rue et auprès des familles
            </h1>
            <p className="text-lg md:text-xl text-white/85 leading-relaxed mb-10 max-w-xl">
              Depuis 2025, Nous'Rire mène maraudes, distributions, collectes alimentaires et
              événements solidaires à Paris et en banlieue, aux côtés des personnes en précarité.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#benevole" className="btn-primary">
                Devenir bénévole
              </a>
              <a href="#calendrier" className="btn-light">
                Voir le calendrier
              </a>
            </div>
          </motion.div>
        </div>

        {/* Carousel controls */}
        <div className="absolute bottom-6 right-4 md:right-8 z-10 flex items-center gap-3">
          <button
            onClick={goToPreviousImage}
            className="rounded-full p-2 border border-white/50 text-white hover:bg-white/15 transition-colors"
            aria-label="Image précédente"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => goToSpecificImage(index)}
                className={`h-2 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'w-6 bg-white' : 'w-2 bg-white/50'}`}
                aria-label={`Image ${index + 1} : ${image.alt}`}
                aria-current={index === currentImageIndex}
              />
            ))}
          </div>
          <button
            onClick={goToNextImage}
            className="rounded-full p-2 border border-white/50 text-white hover:bg-white/15 transition-colors"
            aria-label="Image suivante"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Impact band */}
      <div className="bg-brand text-brand-fg">
        <div className="container mx-auto px-4 py-10 md:py-12 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:divide-x sm:divide-[#2B1A18]/15">
          {stats.map(stat => (
            <StatCounter key={stat.label} stat={stat} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Hero;
